"use client";

import * as React from "react";
import { useRouter, usePathname } from "next/navigation";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowUpRight,
  Send,
  Sparkles,
  X,
  AlertTriangle,
  Loader2,
  PanelRightOpen,
  PanelRight,
} from "lucide-react";

import { FakiMark } from "@/components/copilot/FakiMark";
import { CopilotMarkdown } from "@/components/copilot/CopilotMarkdown";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils/utils";
import { useIsDesktop } from "@/lib/copilot/useIsDesktop";
import { useDocsFaki, DOCS_FAKI_WIDTH } from "./DocsFakiContext";
import {
  findHeadingElement,
  highlightSection,
  slugifyHeading,
} from "./docs-section-utils";

type LinkRef = { title: string; url: string; section?: string };

type Msg = {
  role: "user" | "assistant";
  content: string;
  links?: LinkRef[];
};

const STORAGE_DEVICE = "orkestria-docs-device-id";
const API_BASE =
  process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000/api";

function getOrCreateDeviceId(): string {
  if (typeof window === "undefined") return "";
  let id = window.localStorage.getItem(STORAGE_DEVICE);
  if (id && id.length >= 8) return id;
  id =
    (crypto.randomUUID?.() as string) ??
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  window.localStorage.setItem(STORAGE_DEVICE, id);
  return id;
}

export function FakiDocs() {
  const { open, setOpen, mode, setMode, width, setWidth } = useDocsFaki();
  const isDesktop = useIsDesktop();
  const isImmersed = mode === "immersed" && isDesktop;
  const router = useRouter();
  const pathname = usePathname();

  /**
   * Page-agent: al clicar un link de Faki, navega a la doc y resalta la
   * sección. Si ya estamos en la doc destino, solo hace scroll + highlight.
   */
  const handleNavigate = React.useCallback(
    (l: LinkRef) => {
      const slug = l.section ? slugifyHeading(l.section) : "";
      const targetHash = slug ? `#${slug}` : "";

      // Guarda el texto exacto del section como fallback (por si el slug
      // generado no matchea 1:1 con github-slugger en la página destino).
      if (l.section && slug) {
        try {
          window.sessionStorage.setItem(
            `docs-section-text:${slug}`,
            l.section,
          );
        } catch {}
      }

      if (pathname === l.url) {
        // Mismo path → scroll directo
        if (slug) {
          const el = findHeadingElement(slug, l.section);
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
            setTimeout(() => highlightSection(el), 250);
            try {
              history.replaceState(null, "", `${l.url}${targetHash}`);
            } catch {}
          }
        }
      } else {
        router.push(`${l.url}${targetHash}`);
      }

      // En modo flotante (móvil o desktop floating) cierra el panel para que
      // se vea el highlight. En inmersivo permanece abierto.
      if (!isImmersed) setOpen(false);
    },
    [pathname, router, isImmersed, setOpen],
  );

  const [input, setInput] = React.useState("");
  const [messages, setMessages] = React.useState<Msg[]>([]);
  const [sending, setSending] = React.useState(false);
  const [quota, setQuota] = React.useState<{
    used: number;
    limit: number;
    remaining: number;
  } | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const deviceIdRef = React.useRef<string>("");
  const messagesEndRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    deviceIdRef.current = getOrCreateDeviceId();
  }, []);

  React.useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, setOpen]);

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, sending]);

  const fetchQuota = React.useCallback(async () => {
    const id = deviceIdRef.current;
    if (!id) return;
    try {
      const res = await fetch(
        `${API_BASE}/docs-assistant/quota?deviceId=${encodeURIComponent(id)}`,
      );
      if (res.ok) {
        const data = await res.json();
        setQuota(data);
      }
    } catch {}
  }, []);

  React.useEffect(() => {
    if (open) void fetchQuota();
  }, [open, fetchQuota]);

  const send = async () => {
    const q = input.trim();
    if (!q || sending) return;
    setError(null);
    const userMsg: Msg = { role: "user", content: q };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setSending(true);
    try {
      const res = await fetch(`${API_BASE}/docs-assistant/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          deviceId: deviceIdRef.current,
          question: q,
          history: messages.slice(-6).map((m) => ({
            role: m.role,
            content: m.content,
          })),
        }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        const msg =
          res.status === 429
            ? "Has alcanzado el límite diario (10/día) en este dispositivo. Vuelve mañana."
            : (j?.message as string) ||
              "Faki no pudo contestar ahora. Intenta de nuevo.";
        setError(msg);
        if (res.status === 429) await fetchQuota();
        return;
      }
      const data = (await res.json()) as {
        answer: string;
        links: { title: string; url: string }[];
        quota: { used: number; limit: number; remaining: number };
      };
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: data.answer, links: data.links },
      ]);
      setQuota(data.quota);
    } catch {
      setError("Sin conexión con el servidor.");
    } finally {
      setSending(false);
    }
  };

  // ── Resizer (solo desktop, ambos modos) ──
  const draggingRef = React.useRef(false);
  const startXRef = React.useRef(0);
  const startWRef = React.useRef(0);
  const onResizeStart = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    draggingRef.current = true;
    startXRef.current = e.clientX;
    startWRef.current = width;
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";

    const onMove = (ev: PointerEvent) => {
      if (!draggingRef.current) return;
      const dx = startXRef.current - ev.clientX;
      setWidth(startWRef.current + dx);
    };
    const onUp = () => {
      draggingRef.current = false;
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  return (
    <>
      {/* ── Launcher ── */}
      <AnimatePresence>
        {!open && (
          <motion.button
            key="launcher"
            type="button"
            onClick={() => setOpen(true)}
            aria-label="Abrir Faki"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 22 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="fixed bottom-5 right-5 z-40 flex h-12 w-12 cursor-pointer items-center justify-center overflow-hidden rounded-full bg-primary p-2.5 text-primary-foreground shadow-lg shadow-primary/30 transition"
          >
            <FakiMark forceLight className="h-full w-full object-contain" />
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-20" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* ── Panel ── */}
      <AnimatePresence>
        {open && (
          <>
            {/* Backdrop solo en floating (móvil o desktop) — en immersed no
                porque el panel "empuja" el contenido y se puede interactuar
                con el resto. */}
            {!isImmersed && (
              <motion.div
                key="backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18 }}
                onClick={() => setOpen(false)}
                className="fixed inset-0 z-40 bg-background/70 backdrop-blur-sm"
              />
            )}

            <motion.aside
              key="panel"
              role="complementary"
              aria-label="Faki · Asistente de documentación"
              initial={{ x: "100%", opacity: 0.5 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: "100%", opacity: 0.5 }}
              transition={{ type: "spring", stiffness: 320, damping: 32 }}
              style={isDesktop ? { width } : undefined}
              className={cn(
                "fixed right-0 top-0 z-50 flex h-dvh flex-col border-l border-border/60 bg-card shadow-2xl",
                !isDesktop && "w-full max-w-md",
              )}
            >
              {/* Resizer (solo desktop) */}
              {isDesktop && (
                <div
                  role="separator"
                  aria-orientation="vertical"
                  aria-label="Redimensionar Faki"
                  onPointerDown={onResizeStart}
                  onDoubleClick={() => setWidth(DOCS_FAKI_WIDTH.DEFAULT_W)}
                  className="group absolute left-0 top-0 z-10 h-full w-1.5 -translate-x-1/2 cursor-col-resize"
                >
                  <div className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-border transition group-hover:bg-primary/60 group-active:bg-primary" />
                </div>
              )}

              {/* Header */}
              <div className="flex items-center justify-between gap-2 border-b border-border/60 px-4 py-3">
                <div className="flex items-center gap-2.5">
                  <span className="relative inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary/10">
                    <FakiMark forceLight className="h-6 w-6 object-contain" />
                  </span>
                  <div className="leading-tight">
                    <p className="text-sm font-semibold tracking-tight text-foreground">
                      Faki
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      Asistente de documentación
                      {quota && (
                        <>
                          {" · "}
                          <span className="font-medium text-foreground">
                            {quota.remaining}/{quota.limit}
                          </span>{" "}
                          hoy
                        </>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {isDesktop && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      onClick={() =>
                        setMode(mode === "immersed" ? "floating" : "immersed")
                      }
                      aria-label={
                        mode === "immersed"
                          ? "Cambiar a modo flotante"
                          : "Cambiar a modo inmersivo"
                      }
                      title={
                        mode === "immersed"
                          ? "Flotante (sobre el contenido)"
                          : "Inmersivo (empuja el contenido)"
                      }
                    >
                      {mode === "immersed" ? (
                        <PanelRight className="h-4 w-4" />
                      ) : (
                        <PanelRightOpen className="h-4 w-4" />
                      )}
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => setOpen(false)}
                    aria-label="Cerrar"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto px-4 py-4">
                {messages.length === 0 && !error && (
                  <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
                    <span className="relative inline-flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                      <FakiMark forceLight className="h-10 w-10 object-contain" />
                    </span>
                    <div className="space-y-2">
                      <Badge
                        variant="outline"
                        className="gap-1 border-primary/40 bg-primary/10 text-primary"
                      >
                        <Sparkles className="h-3 w-3" />
                        Pregúntale a Faki
                      </Badge>
                      <p className="max-w-sm text-sm text-muted-foreground">
                        Conozco toda la documentación. Hazme una pregunta y te
                        llevo a la sección exacta que la responde.
                      </p>
                    </div>
                    <div className="grid w-full max-w-xs grid-cols-1 gap-2 pt-2">
                      {[
                        "¿Cómo personalizo los accesos directos del navbar móvil?",
                        "¿Qué cambió en la versión 0.3.1-beta?",
                        "¿Cómo abro y cierro caja del día?",
                      ].map((s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => setInput(s)}
                          className="cursor-pointer rounded-md border border-border/60 bg-card px-3 py-2 text-left text-[11px] text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="space-y-3">
                  {messages.map((m, i) => (
                    <div
                      key={i}
                      className={cn(
                        "flex",
                        m.role === "user" ? "justify-end" : "justify-start",
                      )}
                    >
                      <div
                        className={cn(
                          "max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed",
                          m.role === "user"
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted/60 text-foreground",
                        )}
                      >
                        <CopilotMarkdown inverse={m.role === "user"}>
                          {m.content}
                        </CopilotMarkdown>
                        {m.links && m.links.length > 0 && (
                          <div className="mt-2.5 space-y-1.5">
                            {m.links.map((l, idx) => (
                              <button
                                key={`${l.url}-${idx}`}
                                type="button"
                                onClick={() => handleNavigate(l)}
                                className="group inline-flex max-w-full cursor-pointer items-center gap-1.5 rounded-md border border-border/60 bg-card px-2.5 py-1 text-xs font-medium text-foreground transition-colors hover:border-primary/40 hover:text-primary"
                                title={
                                  l.section
                                    ? `Llévame a "${l.section}"`
                                    : "Llévame ahí"
                                }
                              >
                                <ArrowUpRight className="h-3 w-3 shrink-0" />
                                <span className="truncate">
                                  {l.title}
                                  {l.section && (
                                    <span className="ml-1 text-muted-foreground group-hover:text-primary/80">
                                      · {l.section}
                                    </span>
                                  )}
                                </span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {sending && (
                    <div className="flex justify-start">
                      <div className="rounded-2xl bg-muted/60 px-3 py-2">
                        <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                      </div>
                    </div>
                  )}
                  {error && (
                    <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              </div>

              {/* Composer */}
              <div className="border-t border-border/60 p-3">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    void send();
                  }}
                  className="flex items-end gap-2"
                >
                  <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        void send();
                      }
                    }}
                    rows={1}
                    disabled={sending || quota?.remaining === 0}
                    placeholder={
                      quota?.remaining === 0
                        ? "Sin consultas restantes hoy"
                        : "Escribe tu pregunta…"
                    }
                    className="min-h-[40px] flex-1 resize-none rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-xs outline-none transition-colors focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/40 disabled:opacity-50"
                  />
                  <Button
                    type="submit"
                    size="icon"
                    disabled={sending || !input.trim() || quota?.remaining === 0}
                    aria-label="Enviar"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </form>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
